<template>
    <div v-for="(column, index) in navigationColumns" :key="index" class="footer-collapse collapse sm:collapse-open sm:block sm:w-1/4">
        <label :for="`footerNav${column.colName}`" class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: column.colName }) }}</label>
        <input
            :id="`footerNav${column.colName}`"
            type="radio"
            name="footer-navigation"
            class="sm:h-auto sm:min-h-0 sm:hidden"
        >

        <div class="collapse-title h-[69px] flex justify-between font-semibold leading-6 border-b border-b-secondary py-3 pl-4 pr-2 sm:h-auto sm:min-h-0 sm:py-0 sm:px-0 sm:mb-6 sm:border-b-transparent">
            <span class="flex items-center w-full">
                {{ column.colName }}
            </span>

            <button
                v-if="(column?.colLinks?.length ?? 0) > 0"
                class="btn btn-ghost btn-circle sm:hidden"
                aria-expanded="false"
                @click="toggleFooterNav(`footerNav${column.colName}`)"
                @keydown.enter="toggleFooterNav(`footerNav${column.colName}`)"
                @keydown.space="toggleFooterNav(`footerNav${column.colName}`); $event.preventDefault()"
            >
                <span class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: column.colName }) }}</span>
                <MiscIcon name="chevron-down" />
            </button>
        </div>

        <div v-if="(column?.colLinks?.length ?? 0) > 0" class="collapse-content p-0 ">
            <ul class="px-4 py-4 border-b border-b-secondary sm:border-b-transparent sm:px-0 sm:py-0">
                <li v-for="(child, i) in column.colLinks" :key="i">
                    <template v-if="child.isTargetExtern">
                        <a
                            v-if="hasContent(child.link) && hasContent(child.title)"
                            :href="child.link"
                            :target="child.newTab ? '_blank' : '_self'"
                            rel="noreferrer"
                            class="block text-sm underline hover:no-underline py-2 leading-7 sm:leading-6 sm:py-1"
                        >
                            {{ child.title }}
                        </a>
                    </template>
                    <template v-else>
                        <MiscLink
                            v-if="hasContent(child.link) && hasContent(child.title)"
                            :to="child.link"
                            :target="child.newTab ? '_blank' : null"
                            class="block text-sm underline hover:no-underline py-2 leading-7 sm:leading-6 sm:py-1"
                        >
                            {{ child.title }}
                        </MiscLink>
                    </template>
                </li>
            </ul>
        </div>
    </div>
    <div class="sm:w-1/4">
        <div class="footer-collapse collapse sm:collapse-open sm:block sm:mb-4">
            <!-- EU commercial-guarantee-durability A4G-3350 -->
            <label :for="`euGaranLabel`" class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: $t('layout.footerNavigation.warrantyLabel') }) }}</label>
            <input
                :id="`euGaranLabel`"
                type="radio"
                name="footer-navigation"
                class="sm:h-auto sm:min-h-0 sm:hidden"
            >
            <div class="collapse-title h-[69px] flex justify-between font-semibold leading-6 border-b border-b-secondary py-3 pl-4 pr-2 sm:h-auto sm:min-h-0 sm:py-0 sm:px-0 sm:mb-3 sm:border-b-transparent">
                <span class="flex items-center w-full">
                    {{ $t('layout.footerNavigation.warrantyLabel') }}
                </span>

                <button
                    type="button"
                    class="btn btn-ghost btn-circle sm:hidden"
                    aria-expanded="false"
                    @click="toggleFooterNav('euGaranLabel')"
                    @keydown.enter="toggleFooterNav('euGaranLabel')"
                    @keydown.space="toggleFooterNav('euGaranLabel'); $event.preventDefault()"
                >
                    <span class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: $t('layout.footerNavigation.warrantyLabel') }) }}</span>
                    <MiscIcon name="chevron-down" />
                </button>
            </div>

            <div class="collapse-content p-0 sm:visible sm:h-min sm:min-h-full sm:mb-10">
                <div class="px-4 py-4 border-b border-b-secondary sm:border-b-transparent sm:px-0 sm:py-0 text-sm font-normal">
                    <!-- Mobile: text, image, link -->
                    <div class="sm:hidden">
                        <p class="mb-3">{{ $t('layout.footerNavigation.warrantyLinkText') }}</p>
                        <img
                            src="/images/a4g_2026_gewaehrleistungslabel.png"
                            alt="EU Garantielabel"
                            class="w-full mb-3"
                        >
                        <MiscLink
                            :to="$t('layout.footerNavigation.warrantyLink')"
                            external
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            class="inline-flex link link-primary items-center text-sm gap-[6px]"
                        >
                            {{ $t('product.detail.tabs.warrantyConditionsLink') }}
                            <MiscIcon name="arrow-right" size="xs" class="!w-4 !h-4" />
                        </MiscLink>
                    </div>

                    <!-- Desktop: clickable text that opens modal -->
                    <div class="hidden sm:block">
                        <button
                            type="button"
                            class="text-left underline hover:no-underline cursor-pointer"
                            @click="openWarrantyModal"
                        >
                            {{ $t('layout.footerNavigation.warrantyLinkText') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <LayoutFooterWarrantyModal modal-id="footer-warranty-modal" />
        
        <div class="footer-collapse collapse sm:collapse-open sm:block sm:mb-4">
            <label :for="`footerNavPayment`" class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: 'Payment Methods' }) }}</label>
            <input
                :id="`footerNavPayment`"
                type="radio"
                name="footer-navigation"
                class="sm:h-auto sm:min-h-0 sm:hidden"
            >

            <div class="collapse-title h-[69px] flex justify-between font-semibold leading-6 border-b border-b-secondary py-3 pl-4 pr-2 sm:h-auto sm:min-h-0 sm:py-0 sm:px-0 sm:mb-3 sm:border-b-transparent">
                <span class="flex items-center w-full">
                    {{ $t('layout.footerNavigation.paymentLabel') }}
                </span>

                <button
                    type="button"
                    class="btn btn-ghost btn-circle sm:hidden"
                    aria-expanded="false"
                    @click="toggleFooterNav('footerNavPayment')"
                    @keydown.enter="toggleFooterNav('footerNavPayment')"
                    @keydown.space="toggleFooterNav('footerNavPayment'); $event.preventDefault()"
                >
                    <span class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: 'Payment Methods' }) }}</span>
                    <MiscIcon name="chevron-down" />
                </button>
            </div>

            <div class="collapse-content p-0 sm:visible sm:h-min sm:min-h-full">
                <div class="px-4 py-4 border-b border-b-secondary sm:border-b-transparent sm:px-0 sm:py-0">
                    <MiscLazyLoad class="flex flex-wrap justify-start items-center -mx-1">
                        <img 
                            v-if="data?.mastercard?.value" 
                            src="/images/payments/mastercard.svg" 
                            alt="Mastercard" 
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img 
                            v-if="data?.visa?.value" 
                            src="/images/payments/visa.svg" 
                            alt="Visa" 
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img 
                            v-if="data?.applepay?.value" 
                            src="/images/payments/applepay.svg" 
                            alt="Apple Pay" 
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img 
                            v-if="data?.paypal?.value" 
                            src="/images/payments/paypal.svg" 
                            alt="Paypal" 
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img 
                            v-if="data?.paydirekt?.value" 
                            src="/images/payments/paydirekt.svg" 
                            alt="Paydirect" 
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img 
                            v-if="data?.bancontact?.value" 
                            src="/images/payments/bancontact.svg" 
                            alt="Bancontact" 
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img 
                            v-if="data?.ideal?.value" 
                            src="/images/payments/ideal.svg" 
                            alt="IDEAL" 
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img
                            v-if="data?.eps?.value"
                            src="/images/payments/eps.svg"
                            alt="eps"
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img
                            v-if="data?.blik?.value"
                            src="/images/payments/blik.svg"
                            alt="Blik"
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img
                            v-if="data?.mbway?.value"
                            src="/images/payments/mbway.svg"
                            alt="MB Way"
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img
                            v-if="data?.twint?.value"
                            src="/images/payments/twint.svg"
                            alt="Twint"
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img
                            v-if="data?.klarna?.value"
                            src="/images/payments/klarna.svg"
                            alt="Klarna"
                            class="w-1/4 max-w-[50px] p-1 h-full">
                        <img
                            v-if="data?.googlepay?.value"
                            src="/images/payments/googlepay.svg"
                            alt="Google Pay"
                            class="w-1/4 max-w-[50px] p-1 h-full">
                    </MiscLazyLoad>
                </div>
            </div>
        </div>

        <div class="footer-collapse collapse sm:collapse-open sm:block">
            <label :for="`footerNavShipping`" class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: 'Delivery' }) }}</label>
            <input
                :id="`footerNavShipping`"
                type="radio"
                name="footer-navigation"
                class="sm:h-auto sm:min-h-0 sm:hidden"
            >

            <div class="collapse-title h-[69px] flex justify-between font-semibold leading-6 border-b !border-b-secondary py-3 pl-4 pr-2 sm:h-auto sm:min-h-0 sm:py-0 sm:px-0 sm:mb-3 sm:!border-b-transparent">
                <span class="flex items-center w-full">
                    {{ $t('layout.footerNavigation.shippingLabel') }}
                </span>

                <button
                    type="button"
                    class="btn btn-ghost btn-circle sm:hidden"
                    aria-expanded="false"
                    @click="toggleFooterNav('footerNavShipping')"
                    @keydown.enter="toggleFooterNav('footerNavShipping')"
                    @keydown.space="toggleFooterNav('footerNavShipping'); $event.preventDefault()"
                >
                    <span class="sr-only">{{ $t('layout.footerNavigation.toggleNavigationButton', { accordionTitle: 'Delivery' }) }}</span>
                    <MiscIcon name="chevron-down" />
                </button>
            </div>

            <div class="collapse-content p-0 sm:visible sm:h-min sm:min-h-full">
                <div class="px-4 py-4 border-b border-b-secondary sm:border-b-transparent sm:px-0 sm:py-0">
                    <MiscLazyLoad class="flex flex-wrap justify-start items-center -mx-2">
                        <img 
                            v-if="data?.dhl?.value" 
                            src="/images/shipping/dhl.svg" 
                            alt="DHL" 
                            class="w-1/3 max-w-[100px] p-2 h-full">
                    </MiscLazyLoad>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = defineProps<{
    data: Schemas['CmsElementA4gFooter']['config']
}>()

type FooterLink = NonNullable<Schemas['CmsElementA4gFooter']['config']>['col1']['value'][number]

const navigationColumns = computed(() => {
    return (['col1', 'col2', 'col3'] as const)
        .filter(key => props.data?.[`${key}Name`]?.value && props.data?.[key]?.value?.length > 0)
        .map(key => ({
            colName: props.data?.[`${key}Name`]?.value,
            colLinks: props.data?.[key]?.value.map((item: FooterLink) => ({
                title: item.title,
                link: item.link,
                newTab: item.newTab,
                isTargetExtern: item.isTargetExtern,
            }))
        }))
})

function hasContent (input: string | null) {
    return (input != null && input.trim() != '')
}

function toggleFooterNav(id: string) {
    const radioInput = document.getElementById(id) as HTMLInputElement;
    if (radioInput) {
        radioInput.checked = !radioInput.checked;
        // Update aria-expanded state on the button that triggered this
        const button = document.querySelector(`label[for="${id}"] + input + div button`) as HTMLButtonElement;
        if (button) {
            button.setAttribute('aria-expanded', radioInput.checked ? 'true' : 'false');
        }
    }
}

function openWarrantyModal() {
    const modal = document.getElementById('footer-warranty-modal') as HTMLDialogElement
    modal?.showModal()
}
</script>
